<script setup lang="ts">
import { navigateTo, getProductManufacturerName } from "#imports"
import type { Schemas } from "#shopware"
import type { BoxLayout } from "@shopware/composables"
import { getProductName, getProductRoute } from "@shopware/helpers"
import { useFocusWithin, breakpointsTailwind, useBreakpoints, useResizeObserver, useWindowSize, until } from '@vueuse/core'

interface ProductCardProps {
    product: Schemas["Product"]
    layoutType?: BoxLayout
    showOptions?: boolean
    showWishlist?: boolean
    showBadges?: boolean
    showSizes?: boolean
    showCheapestPrice?: boolean
    hideListingParentPrice?: boolean
    position?: number
    listName?: string,
    imageSizes?: string
    prioritizeFirst?: boolean
}

const props = withDefaults(defineProps<ProductCardProps>(), {
    layoutType: 'standard',
    showOptions: true,
    showWishlist: true,
    showBadges: true,
    showSizes: true,
    position: undefined,
    listName: undefined,
    imageSizes: undefined,
    prioritizeFirst: false,
    showCheapestPrice: false,
    hideListingParentPrice: true
})

const localePath = useLocalePath()
const { formatLink } = useInternationalization(localePath)

const { product } = toRefs(props)

const availableStock = computed<number | undefined>(() => {
    const stock = (product.value.customFields as Record<string, unknown>)?.['gisy_aggregated_variant_stock']
    return typeof stock === 'number' ? stock : undefined
})

const showLowStock = computed<boolean>(() => {
    const stock = availableStock.value
    return typeof stock === 'number' && stock < 4
})

const isParentProduct = computed<boolean>(() => !product.value.parentId)

// Get Nuxt app instance
const nuxtApp = useNuxtApp()

// Additional information on hover
const productCard = ref<HTMLElement>()
const initialProductCardHeight = ref<number>(0)
const productCardContent = ref<HTMLElement>()
const productCardInteractive = ref<HTMLElement>()
const productCardInteractiveHeight = ref<number>(0)
const cardActive = ref<boolean>(false)
const { focused } = useFocusWithin(productCard)
const breakpoints = useBreakpoints(breakpointsTailwind)
const lgAndLarger = breakpoints.greaterOrEqual('lg')
const isTransitioning = ref(false)
const mounted = ref(false)
const imageLoaded = ref(false)
const { width } = useWindowSize()

watch(focused, (focused) => {
    if (focused && lgAndLarger.value) {
        cardActive.value = true
    } else {
        cardActive.value = false
    }
})

watch(width, () => {
    initialProductCardHeight.value = productCard.value?.offsetHeight ?? 0
})

const onImageLoaded = () => {
    imageLoaded.value = true
}

onMounted(async () => {
    try {
        // Wait for image to load before calculations
        if (!imageLoaded.value) {
            await until(imageLoaded).toBe(true, { timeout: 1000 })
        }

        await nextTick()
        initialProductCardHeight.value = productCard.value?.offsetHeight ?? 0
        productCardInteractiveHeight.value = getChildHeightSum(productCardInteractive.value?.children)
    } catch (error) {
        console.warn('Error during mount process:', error)
        // Set fallback values
        initialProductCardHeight.value = 300
        productCardInteractiveHeight.value = 60
    } finally {
        mounted.value = true
    }
})

useResizeObserver(productCard, () => {
    try {
        productCardInteractiveHeight.value = getChildHeightSum(productCardInteractive.value?.children)
    } catch (error) {
        console.warn('Error recalculating card dimensions:', error)
    }
})

function getChildHeightSum(children: HTMLCollection | undefined): number {
    if (!children) return 0
    
    try {
        return Array.from(children).reduce((sum, child) => {
            const height = child.getBoundingClientRect()?.height || 0
            return sum + height
        }, 0)
    } catch (error) {
        console.warn('Error calculating child heights:', error)
        return 0
    }
}

// Handle left click (regular navigation)
function handleCardClick (event: MouseEvent) {
    // Only handle left click (button 0)
    if (event.button === 0) {
        // Track product click if position and listName are provided
        const price = product.value.calculatedPrice?.unitPrice || 0
        const productName = getProductName({ product: product.value }) || ''

        nuxtApp.callHook('nuxtware:productCard:selected', {
            product: {
                id: product.value.id,
                name: productName,
                price: price,
                brand: getProductManufacturerName({ product: product.value }) ?? undefined,
                category: product.value.categories?.[0]?.name
            },
            listName: props.listName || undefined,
            position: props.position || undefined
        })

        navigateTo(formatLink(getProductRoute(product.value)))
    }
}

// Handle middle click (open in new tab)
function handleAuxClick (event: MouseEvent) {
    // Middle click (button 1)
    if (event.button === 1) {
        event.preventDefault()
        const route = formatLink(getProductRoute(product.value))
        // Convert route object to string path
        const url = typeof route === 'string' ? route : route.path || localePath(getProductRoute(product.value))
        window.open(url, '_blank')
    }
}

const productDisplayName = computed(() => {
    let name = getProductName({ product: product.value })

    if(name && product.value?.manufacturer?.name) {
        name = name?.replace(product.value.manufacturer.name, '')
    }

    return name
})
</script>

<template>
    <div class="product-card-stage relative">
        <div v-if="mounted" class="product-card-placeholder" :style="`height: ${initialProductCardHeight}px;`" />

        <article
            ref="productCard"
            class="
                product-card w-full top-0 left-0 bg-base-100 lg:border lg:border-transparent hover:border-border cursor-pointer overflow-hidden p-1 lg:p-3 hover:z-50 focus:z-50
                transition-all
                focus-style
                lg:hover:custom-box-shadow
            "
            :class="mounted ? 'absolute' : 'relative'"
            data-testid="product-card"
            tabindex="0"
            @mouseover="lgAndLarger ? cardActive = true : null"
            @mouseleave="lgAndLarger? cardActive = false : null"
            @click="handleCardClick"
            @auxclick="handleAuxClick"
            @keydown.enter="navigateTo(formatLink(getProductRoute(product)))"
        >
            <RouterLink 
                :to="formatLink(getProductRoute(product))"
                class="sr-only"
                :aria-label="$t('product.viewDetailsFor', { name: getProductName({ product }) })"
                tabindex="-1"
            >
                {{ $t('product.viewDetailsFor', { name: getProductName({ product }) }) }}
            </RouterLink>

            <template v-if="showBadges || showWishlist">
                <div 
                    class="flex items-end md:items-start md:absolute md:left-3 md:right-3 z-20 pb-1 px-1 lg:px-2"
                    :class="[{'justify-between' : showBadges && showWishlist}, {'justify-start' : showBadges && !showWishlist}, {'justify-end' : !showBadges && showWishlist} ]"
                >
                    <ProductBadges
                        v-if="showBadges"
                        :product="product"
                        :show-cheapest-price="showCheapestPrice"
                        variant="floating"
                        class="relative left-auto md:py-3"
                    />

                    <ProductWishlistToggle 
                        v-if="showWishlist"
                        :product="product"
                        variant="icon"
                        class="relative right-auto items-end md:items-center"
                    />
                </div>
            </template>

            <div class="relative p-4 text-center bg-base-300 aspect-[8/9]">
                <ProductCardImage
                    v-if="product?.cover?.media"
                    :product="product"
                    :layout-type="layoutType"
                    :image-sizes="imageSizes"
                    :position="position"
                    :prioritize-first="prioritizeFirst"
                    class="relative z-10 m-auto mix-blend-multiply h-full"
                    @loaded="onImageLoaded"
                />

                <div v-else class="aspect-[8/9] flex flex-col justify-center items-center bg-base-300 overflow-hidden text-base-500">
                    <FoundationIcon name="image" size="2xl" />
                </div>
            </div>

            <div 
                ref="productCardContent"
                class="flex flex-col w-full relative cursor-auto transition-all duration-500 pt-2 lg:py-2 bg-base-100 min-h-38"
                :class="{ 'z-20': isTransitioning }"
                @click.stop="() => null"
            >

                <!-- Product manufacturer -->
                <h3
                    v-if="getProductManufacturerName({ product })"
                    class="font-bold text-center text-sm lg:text-base mb-1 lg:mb-0"
                    data-testid="product-card-manufacturer"
                >
                    {{ getProductManufacturerName({ product }) }}
                </h3>

                <!-- Product name -->
                <p
                    class="text-xs lg:text-sm text-center mb-4"
                    data-testid="product-card-name"
                >
                    {{ productDisplayName }}
                </p>

                <!-- Price section -->
                <ProductPrice
                    :product="product"
                    :hide-listing-parent-price="isParentProduct"
                    :show-cheapest-price="showCheapestPrice"
                    size="small"
                    class="min-h-6 mt-auto"
                />

                <div v-if="showLowStock" class="text-xs lg:text-sm text-center mb-1">
                    {{ $t('product.availableStock') }}
                </div>
            </div> 

            <ProductSizes
                v-if="cardActive && showSizes"
                :product="product"
                items-container-class="px-4 py-2 justify-center"
                item-class="me-1 mb-1 bg-base-200"
            />
        </article>
    </div>
</template>
